import React from 'react';
import moment from 'moment';
import { Calendar, RefreshCw, Printer, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const OperationsHeader = ({
  selectedDate,
  onDateChange,
  onRefresh,
  onPrint,
  loading = false,
}) => {
  const { t } = useTranslation();

  const goToPreviousDay = () => {
    onDateChange(moment(selectedDate, 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD'));
  };

  const goToNextDay = () => {
    onDateChange(moment(selectedDate, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD'));
  };

  const goToToday = () => {
    onDateChange(moment().format('YYYY-MM-DD'));
  };

  const isToday = selectedDate === moment().format('YYYY-MM-DD');
  const isYesterday = selectedDate === moment().subtract(1, 'day').format('YYYY-MM-DD');
  const isTomorrow = selectedDate === moment().add(1, 'day').format('YYYY-MM-DD');

  const getDateLabel = () => {
    // use flat translation keys that we add to the locales
    if (isToday) return t('today');
    if (isYesterday) return t('yesterday');
    if (isTomorrow) return t('tomorrow');
    return moment(selectedDate, 'YYYY-MM-DD').format('LL');
  };

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100 mb-4 sm:mb-6 max-h-[90vh] overflow-y-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        {/* Title and Date */}
        <div className="sticky top-0 bg-white">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            {t('operationsTitle')}
          </h1>
          <p className="text-sm sm:text-base text-slate-600">
            {t('operationsSubtitle')}
          </p>
        </div>

        {/* Date Navigation and Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 sm:rtl:space-x-reverse sticky top-0 bg-white">
          {/* Date Navigation */}
          <div className="flex items-center space-x-2 rtl:space-x-reverse bg-slate-50 rounded-xl p-1">
            <button
              onClick={goToPreviousDay}
              className="p-2 text-slate-600 hover:bg-white hover:text-slate-900 rounded-lg transition-colors duration-200"
            >
              <ChevronRight size={18} />

            </button>

            <div className="flex items-center space-x-3 rtl:space-x-reverse px-3">
              <div className="text-center">
                <div className="text-sm font-semibold text-slate-900">
                  {getDateLabel()}
                </div>
                <div className="text-xs text-slate-500">
                  {moment(selectedDate, 'YYYY-MM-DD').format('ddd, MMM D')}
                </div>
              </div>

              <div className="relative">
                {/* <Calendar size={16} className="absolute start-3 top-1/2 transform -translate-y-1/2 text-slate-400" /> */}
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => onDateChange(e.target.value)}
                  className="ps-10 pe-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
              </div>
            </div>

            <button
              onClick={goToNextDay}
              className="p-2 text-slate-600 hover:bg-white hover:text-slate-900 rounded-lg transition-colors duration-200"
            >
              <ChevronLeft size={18} />

            </button>
          </div>

          {/* Quick Date Buttons */}
          <div className="flex space-x-2 rtl:space-x-reverse">
            {!isToday && (
              <button
                onClick={goToToday}
                className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors duration-200"
              >
                {t('goToToday')}
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-2 rtl:space-x-reverse">
            <button
              onClick={onRefresh}
              disabled={loading}
              className="flex items-center space-x-2 rtl:space-x-reverse px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors duration-200 disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">{t('refreshOperations')}</span>
            </button>

            <button
              onClick={onPrint}
              className="flex items-center space-x-2 rtl:space-x-reverse px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
            >
              <Printer size={16} />
              <span className="hidden sm:inline">{t('operationsPrintReport')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};